/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  createSolid,
  hasGradient,
  generatePatternStyles,
  getHexFromSolid,
  getSolidFromHex,
  isPatternEqual,
  createSolidFromString,
} from '@web-stories-wp/patterns';
import { OrderedSet } from 'immutable';
import { CharacterMetadata, EditorState } from 'draft-js';

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../constants';
import { NONE, COLOR, COLOR_GRADIENT, COLOR_ID } from '../customConstants';
import {
  togglePrefixStyle,
  getPrefixStylesInSelection,
  setExclusiveInlineStyle,
} from '../styleManipulation';
import { isStyle, getVariable } from './util';
import { getIdAndColorFromStringWithVariable } from '../../../app/savedColors/helpers';
import {
  GRADIENT_PATTERN_STYLE_PROP,
  encodeGradientPattern,
  decodeGradientPattern,
  getGradientFallbackColor,
  getGradientPatternStyleValue,
} from '../../../utils/textGradient';
/*
 * Color uses PREFIX-XXXXXXXX where XXXXXXXX is the 8 digit
 * hex represenation of the RGBA color.
 */
const styleToColor = (styles) => {
  const colorStyle = styles.find((someStyle) => isStyle(someStyle, COLOR));
  const savedColorStyle = styles.find((someStyle) =>
    isStyle(someStyle, COLOR_ID)
  );
  const color = colorStyle ? getVariable(colorStyle, COLOR) : null;
  const colorId = savedColorStyle
    ? getVariable(savedColorStyle, COLOR_ID)
    : null;

  return {
    colorId: colorId === 'null' ? null : colorId,
    color: getSolidFromHex(color),
  };
};

const colorToStyle = (color) => `${COLOR}-${getHexFromSolid(color)}`;
const colorIdToStyle = (colorId) => `${COLOR_ID}-${colorId}`;
const gradientToStyle = (pattern) =>
  `${COLOR_GRADIENT}-${encodeGradientPattern(pattern)}`;
const styleToGradient = (style) =>
  decodeGradientPattern(getVariable(style, COLOR_GRADIENT));

function elementToStyle(element) {
  const isSpan = element.tagName.toLowerCase() === 'span';
  const gradientValue = isSpan
    ? getGradientPatternStyleValue(element.style)
    : '';

  if (gradientValue) {
    const gradient = decodeGradientPattern(gradientValue);
    if (gradient) {
      return gradientToStyle(gradient);
    }
  }

  const { color: rawColor, colorId } = getIdAndColorFromStringWithVariable(
    element.style.color
  );
  const hasColor = Boolean(rawColor);

  if (isSpan && hasColor) {
    const solid = createSolidFromString(rawColor);
    return [colorToStyle(solid), colorIdToStyle(colorId)];
  }

  return null;
}

function stylesToCSS(styles) {
  const gradientStyle = styles.find((someStyle) =>
    isStyle(someStyle, COLOR_GRADIENT)
  );

  if (gradientStyle) {
    const encodedGradient = getVariable(gradientStyle, COLOR_GRADIENT);
    const gradient = decodeGradientPattern(encodedGradient);
    if (!gradient) {
      return null;
    }
    return {
      [GRADIENT_PATTERN_STYLE_PROP]: encodedGradient,
      ...generatePatternStyles(
        { ...gradient, geometryVersion: undefined },
        'background'
      ),
      color: getGradientFallbackColor(gradient) || '#000000',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    };
  }

  const style =
    styles.filter(
      (someStyle) => isStyle(someStyle, COLOR) || isStyle(someStyle, COLOR_ID)
    ) || [];

  if (OrderedSet.isOrderedSet(style) ? style.size === 0 : style.length === 0) {
    return null;
  }

  let color;
  try {
    color = styleToColor(style);
  } catch (e) {
    return null;
  }

  return generatePatternStyles(color.color, 'color', color.colorId);
}

function getColor(editorState) {
  const gradientStyles = getPrefixStylesInSelection(
    editorState,
    COLOR_GRADIENT
  );
  const gradientStyle = gradientStyles.find((style) => style !== NONE);
  const colorStyles = getPrefixStylesInSelection(editorState, COLOR);
  const colorIdStyles = getPrefixStylesInSelection(editorState, COLOR_ID);
  const hasSolidStyle = colorStyles.some((style) => style !== NONE);

  if (gradientStyle) {
    if (gradientStyles.length > 1 || hasSolidStyle) {
      return MULTIPLE_VALUE;
    }
    return styleToGradient(gradientStyle) || MULTIPLE_VALUE;
  }

  const styles = colorStyles;

  if (styles.length > 1) {
    return MULTIPLE_VALUE;
  }

  const colorStyle = styles[0];

  if (colorStyle === NONE) {
    return createSolid(0, 0, 0);
  }

  const colorIdStyle = colorIdStyles[0];
  const { color, colorId } = styleToColor([colorStyle, colorIdStyle]);

  return { ...color, id: colorId };
}

function setColor(editorState, color) {
  if (hasGradient(color)) {
    const gradientStyle = gradientToStyle(color);
    const stylesToRemove = [COLOR, COLOR_ID, COLOR_GRADIENT]
      .flatMap((prefix) => getPrefixStylesInSelection(editorState, prefix))
      .filter((style) => style !== NONE);
    // Replace the selected colors in one Draft history entry so Undo also
    // restores their original solid colors and saved-color references.
    return setExclusiveInlineStyle(editorState, stylesToRemove, gradientStyle);
  }

  const originalState = editorState;
  editorState = togglePrefixStyle(editorState, COLOR_GRADIENT, () => false);

  const isBlack = isPatternEqual(createSolid(0, 0, 0), color);

  // the style util manages conversion
  const getStyleToSet = () => colorToStyle(color);

  if (color.id) {
    const shouldSetStyle = () => true;

    editorState = togglePrefixStyle(editorState, COLOR_ID, shouldSetStyle, () =>
      colorIdToStyle(color.id)
    );
  }

  // opaque black is default, and isn't necessary to set
  const shouldSetStyle = () => !isBlack || !!color.id;

  const updatedState = togglePrefixStyle(
    editorState,
    COLOR,
    shouldSetStyle,
    getStyleToSet
  );
  // A caret changes pending typing styles, not content or history. For a
  // range, commit all color/reference replacements as one undoable action.
  return originalState.getSelection().isCollapsed()
    ? updatedState
    : EditorState.push(
        originalState,
        updatedState.getCurrentContent(),
        'change-inline-style'
      );
}

export function replaceGradientStylesInEditorState(
  editorState,
  { colorId, editedColor, action = 'addOrEdit' }
) {
  if (!editorState || !colorId) {
    return editorState;
  }

  const contentState = editorState.getCurrentContent();
  let hasChanges = false;

  const blockMap = contentState.getBlockMap().map((block) => {
    const characterList = block.getCharacterList();
    const updatedCharacterList = characterList.map((character) => {
      const styles = character.getStyle();
      const gradientStyle = styles.find((style) =>
        isStyle(style, COLOR_GRADIENT)
      );

      if (!gradientStyle) {
        return character;
      }

      const pattern = styleToGradient(gradientStyle);
      if (!pattern || pattern.id !== colorId) {
        return character;
      }

      hasChanges = true;

      let nextCharacter = CharacterMetadata.removeStyle(
        character,
        gradientStyle
      );

      if (action === 'delete') {
        const detachedPattern = { ...pattern };
        delete detachedPattern.id;
        return CharacterMetadata.applyStyle(
          nextCharacter,
          gradientToStyle(detachedPattern)
        );
      }

      if (hasGradient(editedColor)) {
        return CharacterMetadata.applyStyle(
          nextCharacter,
          gradientToStyle(editedColor)
        );
      }

      nextCharacter = CharacterMetadata.applyStyle(
        nextCharacter,
        colorToStyle(editedColor)
      );
      return CharacterMetadata.applyStyle(
        nextCharacter,
        colorIdToStyle(colorId)
      );
    });

    if (updatedCharacterList === characterList) {
      return block;
    }

    return block.set('characterList', updatedCharacterList);
  });

  if (!hasChanges) {
    return editorState;
  }

  const updatedContentState = contentState.set('blockMap', blockMap);
  return EditorState.push(
    editorState,
    updatedContentState,
    'change-inline-style'
  );
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  autoFocus: true,
  getters: {
    color: getColor,
  },
  setters: {
    setColor,
  },
};

export default formatter;
